

















import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { ReturnPackageSortOrder, ReturnPackageSummary } from "../../../generated/contracts";
import Api from "../../../core/api/Api";
import TableViewControlModel, { ToolbarItem } from "../../components/table-view/tableViewModels";
import constants from "../../../core/constants";
import { statusToDisplayNameAndColor, toDomainSortBy } from "../shared/tableHelper";
import TableView from "../../components/table-view/TableView.vue";
import { TableHeader } from "@/ui/atoms/table/tableModels";
import { dateTimeFilter } from "@/ui/filters/dateTimeFilters";
import router from "@/ui/router";

interface ReturnPackageSummaryTableHeader extends TableHeader {
    key: keyof (ReturnPackageSummary);
}

@Component({
    components: { TableView }
})
export default class StoreReturnDeliveryView extends Vue {
    @Prop() private id!: string;
    @Prop() private apiMethod;
    @Prop() private tab!: string;

    private loadFromUrl = this.tab === router.currentRoute.query["tab"];
    private initialSortOrder: keyof (ReturnPackageSummary) = router.currentRoute.query["sortOrder"] && this.loadFromUrl ?
                (router.currentRoute.query["sortOrder"] as keyof (ReturnPackageSummary))! :
                "status";
    private initialSortBy = router.currentRoute.query["sortByAsc"] && this.loadFromUrl ?
                router.currentRoute.query["sortByAsc"] === "true" :
                true;
    private initialSearchTerm = router.currentRoute.query["searchTerm"] && this.loadFromUrl ?
                router.currentRoute.query["searchTerm"] :
                null;
    private initialFromDate = router.currentRoute.query["fromDate"] && this.loadFromUrl ?
                router.currentRoute.query["fromDate"] as string :
                null;
    private initialToDate = router.currentRoute.query["toDate"] && this.loadFromUrl ?
                router.currentRoute.query["toDate"] as string :
                null;
    private tableControlModel: TableViewControlModel = {
        fromDate: this.initialFromDate ? this.initialFromDate : null,
        toDate: this.initialToDate ? this.initialToDate : null,
        searchTerm: this.initialSearchTerm,
        sort: {
            sortOrder: this.initialSortOrder,
            sortByAscending: this.initialSortBy
        }
    };

    private loadingOrderBy = false;
    private loadingMore = false;
    private returnPackageSummaries: ReturnPackageSummary[] = [];

    private mounted() {
        this.load(0, true);
    }

    private get tableHeader(): ReturnPackageSummaryTableHeader[] {
        return [
            { key: "ssccNumber", title: "SSCC", size: 30 },
            { key: "created", title: "Oprettet", size: 30 },
            { key: "deliveredTime", title: "Afhentningstidspunkt", size: 25 },
            { key: "status", title: "Status", size: 15 }
        ];
    }

    private rowClicked(row: ReturnPackageSummary) {
        router.push({ name: "StoreReturnPackage", params: { storeAccountNumber: this.id, id: row.ssccNumber } });
    }

    private tableControlModelChanged(tableControlData: TableViewControlModel) {
        this.tableControlModel = tableControlData;
        this.loadingOrderBy = true;
        this.load();
    }

    private async load(skip = 0, firstLoad = false, take = constants.NoOfRowsAtATime) {
        this.loadingMore = true;
        try {
            const sortByAsc = router.currentRoute.query["sortByAsc"] && firstLoad && this.loadFromUrl ?
                router.currentRoute.query["sortByAsc"] === "true" :
                this.tableControlModel.sort.sortByAscending;

            const sortOrder = router.currentRoute.query["sortOrder"] && firstLoad && this.loadFromUrl ?
                (router.currentRoute.query["sortOrder"] as keyof (ReturnPackageSummary))! :
                (this.tableControlModel.sort.sortOrder as keyof (ReturnPackageSummary))!;

            const searchTerm = router.currentRoute.query["searchTerm"] && firstLoad && this.loadFromUrl ?
                router.currentRoute.query["searchTerm"] :
                this.tableControlModel.searchTerm;

            const fromDate = router.currentRoute.query["fromDate"] && firstLoad && this.loadFromUrl ?
                router.currentRoute.query["fromDate"] as string :
                this.tableControlModel.fromDate;

            const toDate = router.currentRoute.query["toDate"] && firstLoad && this.loadFromUrl ?
                router.currentRoute.query["toDate"] as string :
                this.tableControlModel.toDate;

            this.loadFromUrl = this.tab === router.currentRoute.query["tab"];

            if (!firstLoad && this.loadFromUrl) {
                router.replace({ query: {
                    ...this.$route.query,
                    sortOrder: sortOrder.toString(),
                    sortByAsc: sortByAsc.toString(),
                    searchTerm: searchTerm as string,
                    fromDate: fromDate ? fromDate.toString() : "",
                    toDate: toDate ? toDate.toString() : ""} });
                }
            const returnPackages: ReturnPackageSummary[] = await this.apiMethod(
                this.id,
                this.toSortOrder(sortOrder),
                toDomainSortBy(sortByAsc),
                searchTerm,
                fromDate,
                toDate,
                skip,
                take);
            this.returnPackageSummaries = skip > 0 ? [...this.returnPackageSummaries, ...returnPackages] : returnPackages;
        } finally {
            this.loadingOrderBy = false;
            this.loadingMore = false;
        }
    }

    private toSortOrder(key: keyof (ReturnPackageSummary)): ReturnPackageSortOrder {
        switch (key) {
            case "ssccNumber":
                return ReturnPackageSortOrder.SsccNumber;
            case "deliveredTime":
                return ReturnPackageSortOrder.DeliveredDate;
            case "status":
                return ReturnPackageSortOrder.Status;
            default:
                throw new Error(`unknown key ${key}`);
        }
    }

    private async loadMore(done: () => void) {
        await this.load(this.tableContent.length);
        done();
    }

    private get tableContent(): object[] {
        return this.returnPackageSummaries.map((returnPackageSummary: ReturnPackageSummary) => ({
            ssccNumber: returnPackageSummary.ssccNumber,
            created: dateTimeFilter(returnPackageSummary.created),
            deliveredTime: dateTimeFilter(returnPackageSummary.deliveredTime),
            status: statusToDisplayNameAndColor(returnPackageSummary.status)
        }));
    }

    private get toolbarItems() {
        return [ToolbarItem.TextSearch, ToolbarItem.DatePicker];
    }
}
